import type { TaskLink } from './links';
import type { ISODate } from './dates';

/* ---------- Etapas do quadro ---------- */
export const TASK_STATUSES = ['mapped', 'todo', 'doing', 'waiting', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const COLUMNS: { id: TaskStatus; name: string; hint: string }[] = [
  { id: 'mapped', name: 'Mapeado', hint: 'Demandas identificadas, ainda sem compromisso' },
  { id: 'todo', name: 'A fazer', hint: 'Priorizadas e prontas para começar' },
  { id: 'doing', name: 'Fazendo', hint: 'Em andamento agora' },
  { id: 'waiting', name: 'Aguardando terceiros', hint: 'Dependem de resposta de alguém' },
  { id: 'done', name: 'Finalizado', hint: 'Entregues' },
];
export const statusName = (id: TaskStatus) => (COLUMNS.find(c => c.id === id) ?? COLUMNS[0]).name;

/** Ação rápida "avançar" no cartão. */
export const NEXT_STATUS: Partial<Record<TaskStatus, TaskStatus>> = { mapped: 'todo', todo: 'doing', doing: 'done', waiting: 'doing' };

export const PRIORITIES = ['Nenhuma', 'Baixa', 'Média', 'Alta'] as const;
export type Priority = 0 | 1 | 2 | 3;

/* ---------- Pessoas ---------- */
export type Role = 'admin' | 'editor' | 'viewer';
export interface User {
  id: string;
  name: string;
  email: string;
  color: string | null;
  role: Role;
  active: boolean;
}
export const canWrite = (u: Pick<User, 'role'> | null | undefined) => !!u && u.role !== 'viewer';

/* ---------- Tarefas ---------- */
export interface Subtask {
  id: string;
  text: string;
  done: boolean;
  assigneeId: string | null;
  due: ISODate | null;
}
export interface Waiting {
  who: string;
  followUpOn: ISODate | null;
}
export interface Classification {
  categoryId: string;
  subcategoryId: string | null;
}
export interface TaskOrigin {
  kind: 'map-note';
  key: string;
  noteNumber: number;
  baseDate: string;
}
export interface TaskHistoryEntry {
  at: string;
  text: string;
  byId: string | null;
}
export interface Task {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  projectId: string | null;
  assigneeId: string | null;
  priority: Priority;
  due: ISODate | null;
  position: number;
  waiting: Waiting | null;
  themeIds: string[];
  entityIds: string[];
  classification: Classification | null;
  subtasks: Subtask[];
  /** Pastas, arquivos e conversas ligados (SharePoint, OneDrive, Teams, web). */
  links: TaskLink[];
  routineId: string | null;
  routineDate: ISODate | null;
  origin: TaskOrigin | null;
  createdAt: string;
  createdById: string | null;
  enteredStatusAt: string;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string;
  history?: TaskHistoryEntry[];
}

/* ---------- Configuração da área ---------- */
export interface Project { id: string; name: string; color: string; position: number }
export interface Theme { id: string; name: string; color: string; position: number }
export interface Subcategory { id: string; name: string }
export interface Category { id: string; name: string; position: number; subcategories: Subcategory[] }
export interface WorkspaceSettings {
  wipLimit: number;
  projects: Project[];
  themes: Theme[];
  categories: Category[];
}

export const PALETTE = ['#EE6F2D', '#1F1F1F', '#555555', '#2F6F8F', '#3D8B5F', '#7C5AA6', '#C29A1E', '#B84A3A'];
export const AVATAR_COLORS = ['#555555', '#2F6F8F', '#3D8B5F', '#7C5AA6', '#B84A3A', '#8A6A1E'];
export const SUGGESTED_TAXONOMY: [string, string[]][] = [
  ['Operação', ['Rotina', 'Suporte', 'Incidente']],
  ['Melhoria', ['Processo', 'Ferramenta', 'Documentação']],
  ['Projeto', ['Planejamento', 'Execução', 'Entrega']],
  ['Relacionamento', ['Cliente interno', 'Fornecedor', 'Liderança']],
];

export function avatarColor(id: string, explicit?: string | null) {
  if (explicit) return explicit;
  return AVATAR_COLORS[[...id].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
}

/* ---------- Rotinas ---------- */
export type Frequency =
  | { type: 'daily'; interval: number; businessDaysOnly: boolean }
  | { type: 'weekly'; interval: number; weekdays: number[] }
  | { type: 'monthly'; interval: number; dayOfMonth: number | 'last' }
  | { type: 'yearly'; interval: number };

export interface Routine {
  id: string;
  title: string;
  description: string;
  projectId: string | null;
  assigneeId: string | null;
  priority: Priority;
  themeIds: string[];
  entityIds: string[];
  classification: Classification | null;
  startStatus: Extract<TaskStatus, 'mapped' | 'todo' | 'doing'>;
  frequency: Frequency;
  startsOn: ISODate;
  leadDays: number;
  checklist: string[];
  active: boolean;
  generatedThrough: ISODate | null;
  createdById: string | null;
  createdAt: string;
}

/* ---------- Agenda ---------- */
export const CALENDARS = [
  { id: 'team', name: 'Equipe', color: '#EE6F2D' },
  { id: 'meetings', name: 'Reuniões', color: '#555555' },
  { id: 'clients', name: 'Clientes', color: '#2F6F8F' },
] as const;
export type CalendarId = (typeof CALENDARS)[number]['id'];
/** Agenda do Outlook de cada pessoa (só ela vê; somente leitura). */
export const OUTLOOK_CALENDAR = { id: 'outlook', name: 'Outlook', color: '#0F6CBD' } as const;
export type AgendaCalendarId = CalendarId | typeof OUTLOOK_CALENDAR.id;
export const calendarOf = (id: string) => (id === OUTLOOK_CALENDAR.id ? OUTLOOK_CALENDAR : CALENDARS.find(c => c.id === id) ?? CALENDARS[0]);

export interface CalendarEvent {
  id: string;
  title: string;
  location: string;
  date: ISODate;
  start: string;
  end: string;
  calendar: CalendarId;
  createdById: string | null;
}

/** Item da agenda na tela: evento da equipe ou compromisso do Outlook (externo, só leitura). */
export interface AgendaEvent extends Omit<CalendarEvent, 'calendar'> {
  calendar: AgendaCalendarId;
  external?: boolean;
  allDay?: boolean;
  /** Marcado como "disponível" no Outlook. */
  free?: boolean;
  url?: string | null;
}

/* ---------- Notas (pessoais) ---------- */
export interface Note {
  id: string;
  title: string;
  body: string;
  pinned: boolean;
  updatedAt: string;
}
